import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';

import Recurrence from 'domains/campaigns/components/Recurrence/Recurrence';
import { Campaign, CampaignChannelLabels, PartialSend, SendRecurrenceFrequency } from 'domains/campaigns/types';
import ArchiveCampaignModal from 'domains/core/components/ArchiveCampaignModal';
import ConfirmationDialog from 'domains/core/components/ConfirmationDialog';
import DateRangeText from 'domains/core/components/DateRangeText';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import InfoSnackbar from 'domains/core/components/Snackbars/InfoSnackbar';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import Table, { ColumnOption } from 'domains/core/components/Table';
import TableEmptyState from 'domains/core/components/Table/TableEmptyState';
import { renderDate } from 'domains/core/components/Table/utils';
import TableOffsetPagination from 'domains/core/components/TableOffsetPagination';
import TruncatedTextWithTooltip from 'domains/core/components/TruncatedTextWithTooltip';
import UnarchiveCampaignModal from 'domains/core/components/UnarchiveCampaignModal';
import MutationKeys from 'hooks/mutations/keys';
import useDeleteCampaign from 'hooks/mutations/useDeleteCampaign';
import useUpdateCampaignStatus from 'hooks/mutations/useUpdateCampaignStatus';
import useMutationStatus from 'hooks/useMutationStatus';
import { CampaignStatus, MutationStatus, SortDirection, URLPaths } from 'models/enums';

import CampaignsTableOptionsCell from './CampaignsTableOptionsCell';
import CampaignStatusText, { getCampaignStatusDisplayText } from './CampaignStatusText';

export type CampaignsTablePaginationProps = {
    page: number;
    totalPages: number;
    onPreviousPage: () => void;
    onNextPage: () => void;
    rowsPerPage: number;
    rowsPerPageOptions: number[];
    onRowsPerPageChange: (rowsPerPage: number) => void;
};

export type Props = {
    campaigns: Campaign<PartialSend>[];
    isLoading?: boolean;
    isRefetching: boolean;
    searchTerm?: string;
    statuses?: CampaignStatus[];
    refetch: () => void;
    totalCampaignCount?: number;
    pagination?: CampaignsTablePaginationProps;
    onCampaignSort?: (sortColumn: string, direction: SortDirection) => void;
    manualCampaignSortBy?: boolean;
};

/**
 * A table used for displaying curations in the curations page.
 *
 * @param campaigns The campaigns data passed in from CurationsPage.
 * @param isRefetching Whether the campaigns data is currently being refetched.
 * @param refetch Function to refetch the campaigns data.
 * @returns The React node created by this component.
 */
const CampaignsTable = ({
    campaigns,
    isLoading,
    isRefetching,
    searchTerm,
    statuses,
    refetch,
    totalCampaignCount,
    pagination,
    onCampaignSort,
    manualCampaignSortBy = false,
}: Props) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [campaign, setCampaign] = useState<Campaign>();
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
    const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
    const [isUnarchiveModalOpen, setIsUnarchiveModalOpen] = useState(false);
    const [pendingEditCampaign, setPendingEditCampaign] = useState<{ id: string; name: string } | null>(null);

    const history = useHistory();

    const deleteCampaignStatus = useMutationStatus(MutationKeys.DELETE_CAMPAIGN);
    const updateCampaignStatusStatus = useMutationStatus(MutationKeys.UPDATE_CAMPAIGN_STATUS);
    const cloneCampaignStatus = useMutationStatus(MutationKeys.CLONE_CAMPAIGN);

    const { mutate: deleteCampaign, isLoading: isDeleteLoading } = useDeleteCampaign(campaign?.id, {
        onError: (error: Error) => setErrorMessage(error.message),
        onSuccess: () => {
            setSuccessMessage(`"${campaign.name}" successfully deleted.`);
            setInfoMessage('');
            refetch();
        },
    });
    const { mutate: pauseCampaign, isLoading: isPauseLoading } = useUpdateCampaignStatus(
        campaign?.id,
        CampaignStatus.PAUSED.toLowerCase(),
        {
            onError: (error: Error) => setErrorMessage(error.message),
            onSuccess: () => {
                setSuccessMessage(`"${campaign?.name}" paused.`);
                setInfoMessage('');
                refetch();
            },
        }
    );
    const { mutate: resumeCampaign, isLoading: isResumeLoading } = useUpdateCampaignStatus(
        campaign?.id,
        CampaignStatus.RESUMED.toLowerCase(),
        {
            onError: (error: Error) => setErrorMessage(error.message),
            onSuccess: () => {
                setSuccessMessage(`"${campaign?.name}" resumed.`);
                setInfoMessage('');
                refetch();
            },
        }
    );

    // Hook for converting scheduled curation to draft and then editing
    const { mutate: convertToDraftAndEdit, isLoading: isConvertingToDraft } = useUpdateCampaignStatus(
        pendingEditCampaign?.id,
        CampaignStatus.DRAFT.toLowerCase(),
        {
            onError: (error: Error) => {
                setErrorMessage(error.message);
                setInfoMessage('');
                setPendingEditCampaign(null);
            },
            onSuccess: () => {
                setInfoMessage('');
                // Navigate to edit page after successful conversion
                if (pendingEditCampaign) {
                    history.push(
                        `${URLPaths.CURATIONS_CUSTOM}/${pendingEditCampaign.id}?status=${CampaignStatus.DRAFT}`
                    );
                }
                setPendingEditCampaign(null);
            },
        }
    );

    // Trigger the convert to draft mutation when pendingEditCampaign is set
    useEffect(() => {
        if (pendingEditCampaign?.id) {
            convertToDraftAndEdit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingEditCampaign?.id]);

    const toggleUpdateCampaignModal = useCallback(
        ({
            campaign,
            onClick,
            onConfirm,
        }: {
            campaign?: Campaign;
            onClick: (isModalOpen: boolean) => void;
            onConfirm?: () => void;
        }) => {
            onClick(!!campaign);
            onConfirm ? onConfirm() : setCampaign(campaign);
        },
        [setCampaign]
    );

    const isDeleteError = deleteCampaignStatus === MutationStatus.ERROR;
    const isUpdateError = updateCampaignStatusStatus === MutationStatus.ERROR;
    const isCloneError = cloneCampaignStatus === MutationStatus.ERROR;

    const isDeleteSuccess = deleteCampaignStatus === MutationStatus.SUCCESS;
    const isUpdateSuccess = updateCampaignStatusStatus === MutationStatus.SUCCESS;
    const isCloneSuccess = cloneCampaignStatus === MutationStatus.SUCCESS;

    const columns: ColumnOption[] = useMemo(
        () => [
            {
                Header: 'name',
                accessor: 'name',
                align: 'left',
                customSort: 'name',
                Cell: (row: any) => <TruncatedTextWithTooltip value={row.cell.value} />,
            },
            {
                Header: 'status',
                accessor: 'status',
                align: 'center',
                customSort: 'status',
                sortType: (rowA: any, rowB: any) => {
                    const textA = getCampaignStatusDisplayText(rowA.original);
                    const textB = getCampaignStatusDisplayText(rowB.original);
                    return textA.localeCompare(textB);
                },
                Cell: (row: any) => {
                    const campaign = row.cell.row.original;

                    return <CampaignStatusText campaign={campaign} />;
                },
            },
            {
                Header: 'schedule',
                accessor: (row) => new Date(row.startTime),
                align: 'center',
                id: 'schedule',
                customSort: 'start_time',
                sortType: 'datetime',
                Cell: (row: any) => {
                    const campaign: Campaign = row.cell.row.original;
                    const { startTime, endTime } = campaign;

                    return (
                        <>
                            {campaign.status === CampaignStatus.DRAFT ? (
                                <Typography>Not Scheduled</Typography> // show not scheduled for draft campaigns
                            ) : (
                                <>
                                    <DateRangeText startTime={startTime} endTime={endTime} />
                                    <div data-test={`${campaign.name}-recurrence`}>
                                        <Recurrence
                                            recurrenceFrequency={
                                                campaign.recurrenceFrequency as SendRecurrenceFrequency
                                            }
                                            startDatetime={startTime}
                                            recurrenceDayOfWeek={campaign.recurrenceDayOfWeek}
                                            recurrenceOccurrencePosition={campaign.recurrenceOccurrencePosition}
                                            sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                                        />
                                    </div>
                                </>
                            )}
                        </>
                    );
                },
            },
            {
                Header: 'type',
                accessor: 'channel',
                align: 'center',
                customSort: 'channel',
                Cell: (row: any) => {
                    const { channel, abTestConfig }: Campaign = row.cell.row.original;
                    const channelName = CampaignChannelLabels[channel];
                    const hasABTest = !!abTestConfig;

                    return (
                        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
                            <span>{channelName}</span>
                            {hasABTest && (
                                <Box
                                    component="span"
                                    sx={{
                                        borderRadius: '4px',
                                        border: '1px solid #9DB2FA',
                                        background: 'var(--Primary-Contrast, #FFF)',
                                        padding: '2px 6px',
                                        fontSize: '16px',
                                        fontWeight: '500',
                                        lineHeight: '24px',
                                    }}
                                >
                                    A/B
                                </Box>
                            )}
                        </Box>
                    );
                },
            },
            {
                Header: 'Date Updated',
                accessor: (row) => (row.lastModifiedTimestamp ? new Date(row.lastModifiedTimestamp) : null),
                align: 'center',
                id: 'lastUpdated',
                customSort: 'last_modified_timestamp',
                sortType: 'datetime',
                Cell: renderDate,
            },
            {
                Header: '',
                accessor: 'options',
                align: 'right',
                disableSortBy: true,
                Cell: (value: any) => {
                    const campaign: Campaign = value.row.original;

                    return (
                        <CampaignsTableOptionsCell
                            campaign={campaign}
                            isConvertingToDraft={isConvertingToDraft}
                            refetch={refetch}
                            setCampaign={setCampaign}
                            setErrorMessage={setErrorMessage}
                            setInfoMessage={setInfoMessage}
                            setIsArchiveModalOpen={setIsArchiveModalOpen}
                            setIsDeleteModalOpen={setIsDeleteModalOpen}
                            setIsPauseModalOpen={setIsPauseModalOpen}
                            setIsResumeModalOpen={setIsResumeModalOpen}
                            setIsUnarchiveModalOpen={setIsUnarchiveModalOpen}
                            setPendingEditCampaign={setPendingEditCampaign}
                            setSuccessMessage={setSuccessMessage}
                            toggleUpdateCampaignModal={toggleUpdateCampaignModal}
                        />
                    );
                },
            },
        ],
        [isConvertingToDraft, refetch, toggleUpdateCampaignModal]
    );

    const emptyCount = totalCampaignCount !== undefined ? totalCampaignCount : campaigns.length;

    const campaignsWithTimestamp = useMemo(
        () =>
            campaigns?.map((campaign) => ({
                ...campaign,
                scheduledTimestamp: new Date(campaign.startTime),
            })),
        [campaigns]
    );

    return (
        <>
            {isDeleteError && <ErrorSnackbar errorMessage={errorMessage} />}
            {isUpdateError && <ErrorSnackbar errorMessage={errorMessage} />}
            {isCloneError && <ErrorSnackbar errorMessage={errorMessage} />}
            {infoMessage && <InfoSnackbar successMessage={infoMessage} />}
            {isDeleteSuccess && <SuccessSnackbar successMessage={successMessage} />}
            {isUpdateSuccess && <SuccessSnackbar successMessage={successMessage} />}
            {isCloneSuccess && <SuccessSnackbar successMessage={successMessage} />}
            <ArchiveCampaignModal
                activeItem={campaign}
                handleModalClose={() => setIsArchiveModalOpen(false)}
                isOpen={isArchiveModalOpen}
                refetchTableData={refetch}
                resetActiveItem={() => setCampaign(undefined)}
            />
            <UnarchiveCampaignModal
                activeItem={campaign}
                handleModalClose={() => setIsUnarchiveModalOpen(false)}
                isOpen={isUnarchiveModalOpen}
                refetchTableData={refetch}
                resetActiveItem={() => setCampaign(undefined)}
            />
            <ConfirmationDialog
                dialogProps={{
                    confirmButtonText: 'Delete',
                    contentText: `You are about to delete "${campaign?.name}." This action cannot be undone.`,
                    title: 'Delete Curation',
                }}
                disabled={isDeleteLoading}
                open={isDeleteModalOpen}
                onConfirm={() => {
                    toggleUpdateCampaignModal({ onClick: setIsDeleteModalOpen, onConfirm: deleteCampaign });
                    setInfoMessage(`Deleting "${campaign?.name}"`);
                }}
                onModalClose={() => toggleUpdateCampaignModal({ onClick: setIsDeleteModalOpen })}
            />
            <ConfirmationDialog
                dialogProps={{
                    confirmButtonText: 'Pause',
                    contentText:
                        'Temporarily stop curation actions. Contacts will stay at their current step in the curation flow until the curation is resumed. No new contacts will enter the flow when paused.',
                    title: 'Pause Curation',
                }}
                disabled={isPauseLoading}
                open={isPauseModalOpen}
                onConfirm={() => {
                    toggleUpdateCampaignModal({ onClick: setIsPauseModalOpen, onConfirm: pauseCampaign });
                    setInfoMessage(`Pausing "${campaign?.name}"`);
                }}
                onModalClose={() => toggleUpdateCampaignModal({ onClick: setIsPauseModalOpen })}
            />
            <ConfirmationDialog
                dialogProps={{
                    confirmButtonText: 'Resume',
                    contentText:
                        'Resume curation actions. Contacts will resume the flow at their current step. The audience will be evaluated at the next scheduled time to pull in new contacts.',
                    title: 'Resume Curation',
                }}
                disabled={isResumeLoading}
                open={isResumeModalOpen}
                onConfirm={() => {
                    toggleUpdateCampaignModal({ onClick: setIsResumeModalOpen, onConfirm: resumeCampaign });
                    setInfoMessage(`Resuming "${campaign?.name}"`);
                }}
                onModalClose={() => toggleUpdateCampaignModal({ onClick: setIsResumeModalOpen })}
            />
            <Table
                columns={columns}
                data={campaignsWithTimestamp}
                defaultSortingRules={[{ id: 'lastUpdated', desc: true }]}
                isLoading={isLoading}
                isRefetching={isRefetching}
                refetch={refetch}
                rowDataTestId="curation-row"
                manualSortBy={manualCampaignSortBy}
                onSort={onCampaignSort}
            />
            {pagination && (
                <TableOffsetPagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    onPreviousPage={pagination.onPreviousPage}
                    onNextPage={pagination.onNextPage}
                    rowsPerPage={pagination.rowsPerPage}
                    rowsPerPageOptions={pagination.rowsPerPageOptions}
                    onRowsPerPageChange={pagination.onRowsPerPageChange}
                    testIdPrefix="campaigns-table-pagination"
                />
            )}
            {!isLoading && emptyCount === 0 && !!(searchTerm || statuses?.length) && (
                <TableEmptyState searchTerm={searchTerm} hasActiveFilters={!!statuses?.length} />
            )}
        </>
    );
};

export default memo(CampaignsTable);
